/**
 * Maps a checklist item's `section` (from the BE) to a detail-page route so the
 * preflight "Go To" button can open the right page in a new tab.
 *
 * The FE owns this mapping on purpose: route paths (and, later, which modal to
 * open) are presentation concerns the BE shouldn't know about. The BE only
 * supplies a stable `section` label.
 *
 * Most sections live under the client's detail area (`/clients/:id/:link`).
 * Clinician sections use the authenticated profile route when the target is the
 * current user and the clinician detail route otherwise.
 *
 * Sections with no destination yet are intentionally omitted — the button is
 * disabled for those until a route exists.
 */
export interface SectionNavTarget {
  /** Whose detail area to open. Defaults to "client". "absolute" uses `link` as the full path. */
  entity?: "client" | "clinician" | "absolute";
  /** `:section` URL segment (client route from clientDetailsRoutes.tsx, or clinician tab). For "absolute", the full path including leading slash. */
  link: string;
  /** For clinician routes: "primary" = client's BCBA (default), "current" = logged-in user. */
  clinicianContext?: "primary" | "current";
}

export const SECTION_NAV: Record<string, SectionNavTarget> = {
  "Personal Information": { link: "profile" },
  // Primary clinician (BCBA) details live on the clinician's own profile.
  "Clinician Profile": { entity: "clinician", link: "profile" },
  "Licenses & Certifications": {
    entity: "clinician",
    link: "licenses-certifications",
    clinicianContext: "current",
  },
  "External Support Team": { link: "care-team" },
  "External Providers": { link: "care-team" },
  Insurance: { link: "insurance" },
  Authorizations: { link: "authorizations" },
  "Diagnostic Profile": { link: "medical-history" },
  "Trauma History": { link: "medical-history" },
  Medication: { link: "medical-history" },
  "Intervention History": { link: "medical-history" },
  "Family History": { link: "home-family-life" },
  "Current Drug Use & Criminal History": { link: "home-family-life" },
  "Developmental History": { link: "developmental-milestones" },
  "Education History": { link: "education" },
  Documents: { link: "documents" },
  "Preliminary Therapy Hours": { link: "preliminary-therapy-hours" },
  "Clinical Approach": { link: "care-plan" },
  "Caregiver Involvement": { link: "care-plan" },
  "Transition & Discharge Planning": { link: "care-plan" },
  "Crisis Plan": { link: "care-plan" },
  "Observed Behaviors & Strengths": { link: "strengths-and-areas-of-need" },
  "Assessment Billing": { entity: "absolute", link: "/billing" },
};

/** Returns the route target for a checklist section, or undefined if unmapped. */
export const getSectionNavTarget = (section?: string): SectionNavTarget | undefined =>
  section ? SECTION_NAV[section] : undefined;

/**
 * Builds a route for a clinician section, using the authenticated profile route
 * when the target clinician is the signed-in user.
 */
export const buildClinicianNavUrl = (
  clinicianId: string | number | undefined,
  link: string,
  userClinicianId?: string | number
): string | null => {
  if (!clinicianId) return null;

  const isCurrentUser =
    userClinicianId !== undefined &&
    userClinicianId !== null &&
    String(clinicianId) === String(userClinicianId);

  return isCurrentUser ? `/my-info/${link}` : `/clinicians/${clinicianId}/${link}`;
};

/**
 * Builds the new-tab URL for a section's "Go To" button, or null when it can't
 * be navigated yet (unmapped section, or a clinician target with no clinician id).
 */
export const buildSectionNavUrl = (
  section: string | undefined,
  ids: {
    clientId: string;
    primaryClinicianId?: string | number;
    currentClinicianId?: string | number;
    userClinicianId?: string | number;
  }
): string | null => {
  const target = getSectionNavTarget(section);
  if (!target) return null;
  if (target.entity === "absolute") return target.link;
  if (target.entity === "clinician") {
    const clinicianId =
      target.clinicianContext === "current" ? ids.currentClinicianId : ids.primaryClinicianId;
    return buildClinicianNavUrl(clinicianId, target.link, ids.userClinicianId);
  }
  return `/clients/${ids.clientId}/${target.link}`;
};
